'use client';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { toast, Toaster } from 'sonner';
import { CTAButton } from '~/components/common/cta-button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/lib/utils';
import type {
  ControlNetOptions,
  ImageGenerationOptions,
  ImageTransformationOptions,
} from '~/services/inference/image-generation';
import { FireworksImageSDK } from '~/services/inference/image-generation';
import { Model } from '~/services/models.types';
import { IconAppWindowFilled } from '@tabler/icons-react';
import { Loading } from '../loading';
import { useActiveTabState, useRequestState } from '../request.context';
import { sd3Generate } from './sd3-client';

interface ValidationState {
  isValidPrompt: boolean;
  isValidImage: boolean;
}

export const ImagePlayground = ({
  apiKey,
  model,
  imageMode = 'none',
}: {
  model: Model<'image'>;
  apiKey: string;
  imageMode: 'image-to-image' | 'controlNet' | 'none';
}) => {
  const [loading, setLoading] = React.useState(false);
  const { setSelectedTab } = useRequestState();
  const { requestState: rState, setRequestState } = useActiveTabState();

  const requestState = rState as ControlNetOptions | ImageGenerationOptions | ImageTransformationOptions;

  useEffect(() => {
    setSelectedTab(imageMode === 'none' ? 'image' : imageMode);
  }, []);

  const apiClient = useMemo(() => new FireworksImageSDK(apiKey), [apiKey]);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [inputImage, setInputImage] = useState<File | null>(null);
  const [validation, setValidation] = useState<ValidationState>({ isValidPrompt: true, isValidImage: true });

  async function handleImageUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setInputImage(file);
  }

  function handleInputValidation() {
    const isValidPrompt = requestState.prompt.length > 0;

    setValidation((prevState: ValidationState) => ({
      ...prevState,
      isValidPrompt,
    }));

    return isValidPrompt;
  }

  function handleImageValidation() {
    const isValidImage = inputImage !== null;
    setValidation((prevState: ValidationState) => ({
      ...prevState,
      isValidImage,
    }));

    return isValidImage;
  }

  const handleGenerate = async () => {
    const isValidInput = handleInputValidation();

    if (imageMode !== 'none') {
      const isImageValid = handleImageValidation();
      if (!isImageValid) {
        toast.error('Please upload an image');
        return;
      }
    }

    if (!isValidInput) {
      toast.error('Image Description is required');
      return;
    }

    setLoading(true);
    setImageSrc(null);

    let response: Response | null = null;

    try {
      switch (imageMode) {
        case 'none':
          if (/^sd3(-turbo)?$/iu.test(model.id)) {
            response = await sd3Generate(requestState);
          } else {
            response = await apiClient.generateImage(model.name, requestState);
          }
          break;

        case 'image-to-image':
          if (inputImage === null) {
            throw new Error('processedConditionImage is null');
          }

          if (/^sd3(-turbo)?$/iu.test(model.id)) {
            response = await sd3Generate(requestState, inputImage);
          } else {
            response = await apiClient.transformImage(model.name, inputImage, requestState);
          }
          break;

        case 'controlNet':
          if (inputImage === null) {
            throw new Error('inputImage is null');
          }
          const preprocess = (requestState as ControlNetOptions).control_net_name;
          let processed = inputImage;

          if (preprocess === 'canny') {
            const res = await apiClient.cannyEdgeDetection(model.name, inputImage, {
              accept: requestState.accept,
              // min_val: 100,
              // max_val: 100,
            });

            processed = await res.blob();
          }

          if (preprocess === 'qr') {
            const res = await apiClient.qrCode(model.name, {
              prompt: requestState.prompt,
              accept: requestState.accept,
              // width: 1024,
              // height: 1024,
            });

            processed = await res.blob();
          }

          response = await apiClient.controlNet(model.name, processed, requestState);
          break;
      }

      if (!response) {
        toast.error('No API was able to be called');

        return setLoading(false);
      }
      const blob = await response.blob().catch(() => {
        toast.error('Failed to load image');
        return null;
      });

      try {
        if (blob) {
          const url = URL.createObjectURL(blob);
          setImageSrc(url);
        }
      } catch {
        toast.error('Failed to render image in the UI');
      }
    } catch (e) {
      if (e instanceof Error) {
        console.error(e);

        if (e.message.startsWith('Pre-request API validation failed:')) {
          return toast.error(e.message.replace('Pre-request API validation failed:', ''));
        }
        // Added an extra try/catch to make sure we are safe to JSON.parse the error
        try {
          const { error } = JSON.parse(e.message.split('body:')[1]);
          toast.error(error ? error : 'Something went wrong!');
        } catch (error) {
          toast.error('Something went wrong!');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-[60%] p-8">
      {/* Preview section */}
      <Toaster />
      {/* Chatbox */}
      <div className="border-red flex flex-initial flex-col rounded">
        <div className="grow">
          <Label className="mb-2 flex" htmlFor="prompt">
            Image Prompt<span className={cn({ 'text-red-500': !validation.isValidPrompt })}>*</span>
            {!validation.isValidPrompt && <span className="ml-3 text-xs text-red-400">Image prompt is required</span>}
          </Label>
          <Textarea
            className={cn('mb-4', { 'border-1 border-solid border-red-500': !validation.isValidPrompt })}
            spellCheck={false}
            value={requestState.prompt ?? ''}
            onBlur={handleInputValidation}
            onChange={(e) => setRequestState({ field: 'prompt', value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleGenerate();
                // Call the function to handle the Enter key press here
              }
            }}
            id="prompt"
          />
          {model.id !== 'sd3-turbo' && (
            <>
              <Label className="mb-2 flex gap-3" htmlFor="negative-prompt">
                Negative Prompt
              </Label>
              <Input
                spellCheck={false}
                value={(requestState as any)?.negative_prompt ?? ''}
                onChange={(e) => setRequestState({ field: 'negative_prompt', value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Call the function to handle the Enter key press here
                  }
                }}
                id="negative-prompt"
              />
            </>
          )}
        </div>
      </div>
      <div className="relative mt-4 flex h-full min-h-[300px] flex-auto flex-row overflow-hidden rounded border dark:border-zinc-800">
        {imageMode !== 'none' && (
          <label
            htmlFor="file-upload"
            className="relative flex w-full flex-col items-center justify-center border-r border-dashed bg-gray-100 dark:border-zinc-600 dark:bg-zinc-800"
          >
            {inputImage ? (
              <>
                <Image
                  className="h-[30rem] w-[30rem] cursor-pointer object-contain"
                  src={URL.createObjectURL(inputImage)}
                  height="1024"
                  width="1024"
                  alt="generated image"
                />
                <div className="py-2 pb-4 text-xs text-neutral-400">Click to upload a new image</div>
              </>
            ) : (
              <div className="relative">
                {!validation.isValidImage && (
                  <span className="absolute bottom-[100%] mb-1 text-xs text-red-400">Image is required</span>
                )}
                <span
                  className={cn(
                    'block rounded-md border border-dashed border-neutral-300 px-4 py-4 text-base text-neutral-600 dark:border-zinc-600 dark:text-white',
                    { 'border-red-500  dark:border-red-500': !validation.isValidImage },
                  )}
                >
                  Upload image
                </span>
              </div>
            )}
            <input type="file" id="file-upload" className="hidden" accept="image/*" onChange={handleImageUpload} />
          </label>
        )}
        <OutputWindow loading={loading} imageSrc={imageSrc} selected={imageMode} />
      </div>
      <div className="flex shrink justify-end py-4">
        <div className="my-2 space-x-2">
          <CTAButton
            className="px-8"
            // disabled={requestState.prompt.length < 1}
            onClick={handleGenerate}
          >
            Generate
          </CTAButton>
        </div>
      </div>
      {/* <div className="w-full pt-4 mt-4">
        <div className="flex text-sm space-x-4">
          <div>
            First token <span className="px-1 py-1 rounded-lg bg-gray-200 dark:bg-zinc-600">n/a</span>
          </div>
          <div>
            Average token <span className="px-1 py-1 rounded-lg bg-gray-200 dark:bg-zinc-600">n/a</span>
          </div>
        </div>
      </div> */}
    </div>
  );
};

const DeafultState = () => {
  return (
    <>
      <IconAppWindowFilled className="h-12 w-12 text-zinc-300 dark:text-zinc-600" />
      <div className="text-sm font-medium text-zinc-300 dark:text-zinc-600">Output will be displayed here</div>
    </>
  );
};

const OutputWindow = ({
  loading,
  imageSrc,
  className,
}: {
  loading: boolean;
  imageSrc: string | null;
  selected: any;
  className?: string;
}) => {
  return (
    <div className={cn('flex w-full flex-col items-center justify-center', className)}>
      {loading && <Loading />}
      {!loading && !imageSrc && <DeafultState />}
      {imageSrc && (
        <div className="relative">
          <Image
            src={imageSrc}
            height="1024"
            width="1024"
            className="h-[30rem] w-[30rem] object-contain"
            alt="generated image"
          />
        </div>
      )}
    </div>
  );
};
